import { useState, useEffect } from 'react';
import { useRedeemFlow } from '../../../coordinators/rewards/hooks/useRedeemFlow';
import { useRewardsState } from '../../../coordinators/rewards/hooks/useRewardsState';
import { DiscordSupportLink, FrozenCallout } from './RewardsErrorStates';
import { PointsBadge, RewardIcon } from './RewardIcon';
import { RedeemSuccess } from './RedeemSuccess';
import { RedeemFailure, RedeemOutOfStock } from './RedeemFailure';

interface RedeemPageProps {
  skuId: string;
  onBack: () => void;
  onViewHistory: () => void;
  onBackToRewards: () => void;
}

export const RedeemPage = ({ skuId, onBack, onViewHistory, onBackToRewards }: RedeemPageProps) => {
  const { profile, restockCountdown } = useRewardsState();
  const {
    phase,
    skuDetail: sku,
    redeemResult,
    error,
    loading,
    reserving,
    secondsLeft,
    startReserve,
    confirmRedemption,
    cancel,
    retry,
    retryFetchDetail,
  } = useRedeemFlow(skuId);

  const userBalance = profile?.pointsBalance ?? 0;
  const userFrozen = profile?.frozen ?? false;
  const restockLocalDate = restockCountdown?.localDate || '';

  if (loading) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <p className='animate-pulse text-sm text-[#FFFFFF]/80'>Loading item...</p>
      </div>
    );
  }

  if (error && !sku) {
    return (
      <div className='flex flex-1 flex-col items-center justify-center gap-3 px-4'>
        <p className='text-sm text-[#FCA5A5]'>{error}</p>
        <button type='button' onClick={retryFetchDetail}
          className='rounded-lg border border-[#FFFFFF]/15 bg-[#FFFFFF]/8 px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-80'>
          Retry
        </button>
        <button type='button' onClick={onBack} className='text-sm text-[#FBBF24] underline'>Back to catalogue</button>
        <DiscordSupportLink />
      </div>
    );
  }

  if (!sku) return null;

  if (phase === 'success') {
    return (
      <RedeemSuccess
        sku={sku}
        code={redeemResult?.code}
        onViewHistory={onViewHistory}
        onBackToRewards={onBackToRewards}
        onBackToCatalogue={onBack}
      />
    );
  }

  if (phase === 'oos') {
    return <RedeemOutOfStock restockLabel={restockCountdown?.label || ''} restockDate={restockLocalDate} onBack={onBack} />;
  }

  if (phase === 'failure') {
    return <RedeemFailure error={error} onRetry={retry} onBack={onBack} />;
  }

  const canAfford = userBalance >= sku.pointCost;
  const isDisabled = userFrozen || !sku.inStock || !canAfford;
  const modalOpen = phase === 'confirm' || phase === 'confirming';

  return (
    <>
      <div className='flex min-h-0 flex-1 flex-col' style={{ opacity: modalOpen ? 0.35 : 1, pointerEvents: phase === 'confirming' ? 'none' : undefined }}>
        {/* Back row — disabled during confirm to prevent dropping success */}
        <button type='button' onClick={onBack} disabled={phase === 'confirming'}
          className='mb-3 shrink-0 self-start border-none bg-transparent p-0 text-sm font-semibold text-[#FFFFFF]/90 transition-opacity hover:opacity-80 disabled:opacity-40'>
          &larr; Catalog
        </button>

        {/* Header row */}
        <div className='mb-3 flex shrink-0 items-center'>
          <span className='flex-1 text-[16px] font-semibold text-white'>Redeem reward</span>
          <PointsBadge balance={userBalance} />
        </div>

        {/* Scrollable content */}
        <div className='hr-scroll flex min-h-0 flex-1 flex-col items-center overflow-y-auto'>
          {/* Art — uses hero gradient background, same as hub */}
          <div
            className='relative mx-auto w-full shrink-0'
            style={{
              maxWidth: 240,
              opacity: !sku.inStock ? 0.75 : 1,
            }}
          >
            <img src='/playcast-hero-gradient.svg' alt='' className='block w-full' draggable={false} />
            {sku.imageUrl && (
              <img src={sku.imageUrl} alt={sku.name} className='absolute inset-0 m-auto h-[75%] w-[75%] object-contain' />
            )}
            {!sku.inStock && (
              <div
                className='absolute'
                style={{
                  top: 8, left: 8, padding: '2px 6px', borderRadius: 4,
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                }}
              >
                <span className='text-[10px] text-[#FFFFFF]/70'>Out of stock</span>
              </div>
            )}
          </div>

          {/* Name as H2, centered */}
          <h2 className='text-center text-[16px] font-semibold text-white' style={{ marginTop: 12 }}>{sku.name}</h2>

          {userFrozen ? (
            <div className='w-full' style={{ marginTop: 10 }}>
              <FrozenCallout />
            </div>
          ) : (
            <>
              {/* Tokens required row */}
              <div className='flex w-full items-center justify-between' style={{ marginTop: 12 }}>
                <span className='text-[13px] text-[#FFFFFF]/80'>Tokens required</span>
                <span className='inline-flex items-center gap-1 text-[13px] font-semibold text-white'>
                  <RewardIcon size={13} />{sku.pointCost.toLocaleString()}
                </span>
              </div>

              {/* OOS callout */}
              {!sku.inStock && (
                <div
                  className='w-full'
                  style={{
                    marginTop: 12, padding: '10px 12px', borderRadius: 8,
                    borderLeft: '3px solid rgba(251,191,36,0.6)',
                    background: 'rgba(251,191,36,0.06)',
                  }}
                >
                  <p className='text-[13px] font-semibold text-[#FBBF24]' style={{ marginBottom: 2 }}>
                    &#9888; Out of stock
                  </p>
                  <p className='text-[12px] text-[#FFFFFF]/80' style={{ lineHeight: 1.4 }}>
                    Restock in <span className='font-semibold' style={{ fontFamily: 'monospace' }}>
                      {restockCountdown?.label || '—'}
                    </span> ({restockLocalDate || 'Sunday 12:00 noon PST'}).
                    You can keep earning and return when stock refreshes.
                  </p>
                </div>
              )}

              {!canAfford && sku.inStock && (
                <div
                  className='w-full'
                  style={{
                    marginTop: 12, padding: '10px 12px', borderRadius: 8,
                    borderLeft: '3px solid rgba(251,191,36,0.6)',
                    background: 'rgba(251,191,36,0.06)',
                  }}
                >
                  <p className='text-[13px] font-semibold text-[#FBBF24]' style={{ marginBottom: 2 }}>
                    &#9888; Not enough tokens
                  </p>
                  <p className='text-[12px] text-[#FFFFFF]/80' style={{ lineHeight: 1.4 }}>
                    Need {(sku.pointCost - userBalance).toLocaleString()} more tokens to redeem this reward.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Bottom CTA */}
        <div className='shrink-0 flex flex-col' style={{ marginTop: 12, gap: 8 }}>
          <button type='button' disabled={isDisabled}
            onClick={startReserve}
            style={{ minHeight: 44, borderRadius: 9999, fontSize: 15, padding: '12px 16px' }}
            className={`flex w-full items-center justify-center gap-2 border-none font-semibold transition-transform ${
              isDisabled ? 'cursor-not-allowed bg-[#FFFFFF]/15 text-[#FFFFFF]/40 opacity-55'
                : 'bg-[#FBBF24] text-[#020617] hover:scale-[1.02] active:scale-[0.98]'
            }`}>
            <span>{sku.inStock ? 'Redeem' : 'Unavailable'}</span>
            {sku.inStock && <span className='inline-flex items-center gap-1'><img src={isDisabled ? '/playcast-token-white.svg' : '/playcast-token-black.svg'} alt='' width={18} height={18} />{sku.pointCost.toLocaleString()}</span>}
          </button>
          {(phase === 'detail' || phase === 'expired') && (
            <button type='button' onClick={onBack}
              className='flex w-full items-center justify-center border-none bg-transparent p-0 text-[12px] text-[#FFFFFF]/80 underline transition-opacity hover:opacity-80'
              style={{ textUnderlineOffset: 2 }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {modalOpen && (
        <ConfirmOverlay
          sku={sku}
          userBalance={userBalance}
          reserving={reserving}
          confirming={phase === 'confirming'}
          secondsLeft={secondsLeft}
          onConfirm={confirmRedemption}
          onCancel={cancel}
        />
      )}
    </>
  );
};

const FILL_DURATION_MS = 1_200;
interface ConfirmOverlayProps {
  sku: { name: string; pointCost: number; imageUrl?: string };
  userBalance: number;
  reserving: boolean;
  confirming: boolean;
  secondsLeft: number;
  onConfirm: () => void;
  onCancel: () => void;
}
function ConfirmOverlay({ sku, userBalance, reserving, confirming, secondsLeft, onConfirm, onCancel }: ConfirmOverlayProps) {
  const [fillDone, setFillDone] = useState(false);
  const [animating, setAnimating] = useState(false);
  useEffect(() => {
    if (reserving) { setFillDone(false); setAnimating(false); return; }
    setAnimating(true);
    const timer = setTimeout(() => setFillDone(true), FILL_DURATION_MS);
    return () => clearTimeout(timer);
  }, [reserving]);

  const ready = !reserving && fillDone && !confirming;
  const after = userBalance - sku.pointCost;

  return (
    <>
      <div className='absolute inset-0 z-40 rounded-[12px] bg-[#0b0618] opacity-[0.72]' />
      <div className='absolute inset-0 z-50 flex items-center justify-center p-4'>
        <div className='w-full rounded-[10px] border border-[#FFFFFF]/15 bg-[#0b0618] p-4'>
          <p className='mb-3 text-center text-[15px] font-semibold text-white'>Confirm redeem</p>

          <div className='mb-[14px] flex flex-col gap-2'>
            <div className='flex items-center justify-between'>
              <span className='text-[13px] text-[#FFFFFF]/80'>Tokens required</span>
              <span className='inline-flex items-center gap-1 text-[13px] font-semibold text-white'>
                <RewardIcon size={13} />{sku.pointCost.toLocaleString()}
              </span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-[13px] text-[#FFFFFF]/80'>Your balance</span>
              <span className='inline-flex items-center gap-1 text-[13px] font-semibold text-white'>
                <RewardIcon size={13} />{userBalance.toLocaleString()}
              </span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-[13px] text-[#FFFFFF]/80'>After redeem</span>
              <span className='inline-flex items-center gap-1 text-[13px] font-semibold text-white'>
                <RewardIcon size={13} />{after.toLocaleString()}
              </span>
            </div>
          </div>

          {!reserving && (
            <p className={`mb-[14px] text-center text-xs ${secondsLeft <= 10 ? 'text-[#FCA5A5]' : 'text-[#FFFFFF]/70'}`}>
              {confirming ? 'Completing redemption...' : `Reserved for ${secondsLeft}s`}
            </p>
          )}

          <div className='flex flex-col gap-2'>
            <button
              type='button'
              disabled={!ready}
              onClick={onConfirm}
              className='relative w-full overflow-hidden font-semibold'
              style={{ minHeight: 44, borderRadius: 9999, border: 'none', background: 'rgba(255,255,255,0.08)', color: '#020617' }}
            >
              <span
                className='absolute inset-0 bg-[#FBBF24]'
                style={{
                  borderRadius: 9999,
                  transform: animating || fillDone ? 'scaleX(1)' : 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: animating && !fillDone ? `transform ${FILL_DURATION_MS}ms ease-out` : 'none',
                }}
              />
              <span className='relative z-10 inline-flex items-center justify-center gap-2' style={{ fontSize: 15 }}>
                Redeem <span className='inline-flex items-center gap-1'><img src='/playcast-token-black.svg' alt='' width={18} height={18} />{sku.pointCost.toLocaleString()}</span>
              </span>
            </button>
            {!confirming && (
              <button type='button' onClick={onCancel}
                style={{ minHeight: 44, borderRadius: 9999 }}
                className='flex w-full items-center justify-center border-none bg-transparent text-sm font-medium text-[#FFFFFF]/80 transition-opacity hover:opacity-80'>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
